import {
  FINANCIAL_PAYLOAD_DECIMAL_PLACES,
  roundAwayFromZero,
  roundDecimalHalfUp,
  TAX_AGGREGATE_DECIMAL_PLACES,
} from './decimal-round.util';
import { RoundingPolicy } from '../interfaces/rounding-settings.interface';

export interface InclusiveLineRawAmounts {
  rawLineTotal: number;
  rawLineNet: number;
  rawLineTax: number;
}

export interface InclusiveLineAdjustments {
  discountAmount: number;
  discountIsPercent: boolean;
  chargeAmount: number;
  chargeIsPercent: boolean;
}

export interface InclusiveLineDisplayAmounts extends InclusiveLineRawAmounts {
  lineNet: number;
  lineTax: number;
  lineTotal: number;
}

export interface InclusiveLinePayloadAmounts {
  price: number;
  taxAmount: number;
}

export type DisplayRoundFn = (
  value: number,
  precision: number,
  policy?: RoundingPolicy,
) => number;

/** Top-down inclusive split at line level; never rounds before quantity multiplication. */
export function computeInclusiveRawLineAmounts(
  rawLineTotal: number,
  vatPercent: number,
): InclusiveLineRawAmounts {
  const total = Math.max(0, Number(rawLineTotal) || 0);
  const vat = Number(vatPercent) || 0;
  if (vat <= 0) {
    return { rawLineTotal: total, rawLineNet: total, rawLineTax: 0 };
  }
  const taxFactor = 1 + vat / 100;
  const rawLineNet = total / taxFactor;
  const rawLineTax = total - rawLineNet;
  return { rawLineTotal: total, rawLineNet, rawLineTax };
}

/** Resolve per-unit inclusive gross without premature rounding. */
export function resolveInclusiveUnitGrossPrice(input: {
  unitNetFromForm: number;
  vatPercent: number;
  catalogInclusiveUnitPrice?: number | null;
  matchesCatalogNet?: boolean;
  userTargetUnitGross?: number | string | null;
}): number {
  const userTarget = Number(input.userTargetUnitGross);
  if (Number.isFinite(userTarget) && userTarget > 0) {
    return userTarget;
  }

  const catalog = Number(input.catalogInclusiveUnitPrice);
  if (
    input.matchesCatalogNet &&
    Number.isFinite(catalog) &&
    catalog > 0
  ) {
    return catalog;
  }

  const net = Number(input.unitNetFromForm) || 0;
  const vat = Number(input.vatPercent) || 0;
  if (net > 0 && vat > 0) {
    return net * (1 + vat / 100);
  }
  if (net > 0) {
    return net;
  }
  return 0;
}

/** Inclusive gross × qty, optional line adjustments, then top-down net/tax split. */
export function computeInclusiveLineRawFromUnitGross(
  inclusiveUnitGross: number,
  quantity: number,
  vatPercent: number,
  adjustments: InclusiveLineAdjustments = {
    discountAmount: 0,
    discountIsPercent: true,
    chargeAmount: 0,
    chargeIsPercent: true,
  },
): InclusiveLineRawAmounts {
  const qty = Number(quantity) || 0;
  const unitGross = Number(inclusiveUnitGross) || 0;
  const vat = Number(vatPercent) || 0;
  const taxFactor = vat > 0 ? 1 + vat / 100 : 1;
  const grossTotal = unitGross * qty;

  const lineCharge = adjustments.chargeIsPercent
    ? (grossTotal * Number(adjustments.chargeAmount || 0)) / 100
    : Number(adjustments.chargeAmount || 0) * taxFactor;
  const merchandiseExtended = grossTotal + lineCharge;
  const lineDiscount = adjustments.discountIsPercent
    ? (merchandiseExtended * Number(adjustments.discountAmount || 0)) / 100
    : Number(adjustments.discountAmount || 0) * taxFactor;

  const rawLineTotal = Math.max(0, merchandiseExtended - lineDiscount);
  return computeInclusiveRawLineAmounts(rawLineTotal, vat);
}

/** Round raw inclusive amounts for UI; tax uses subtraction complement. */
export function toInclusiveLineDisplayAmounts(
  raw: InclusiveLineRawAmounts,
  precision: number,
  roundFn: DisplayRoundFn,
  netPolicy: RoundingPolicy = RoundingPolicy.Nearest,
): InclusiveLineDisplayAmounts {
  const lineTotal = roundFn(raw.rawLineTotal, precision, netPolicy);
  const lineNet = roundFn(raw.rawLineNet, precision, netPolicy);
  const lineTax = roundFn(
    lineTotal - lineNet,
    precision,
    RoundingPolicy.Nearest,
  );
  return {
    ...raw,
    lineNet,
    lineTax,
    lineTotal,
  };
}

/** Map raw inclusive line amounts to API payload fields. */
export function toInclusiveLinePayloadAmounts(
  raw: InclusiveLineRawAmounts,
  quantity: number,
  priceDecimalPlaces: number = FINANCIAL_PAYLOAD_DECIMAL_PLACES,
  taxDecimalPlaces: number = TAX_AGGREGATE_DECIMAL_PLACES,
): InclusiveLinePayloadAmounts {
  const qty = Number(quantity) || 0;
  const unitNet = qty > 0 ? raw.rawLineNet / qty : 0;
  return {
    price: roundDecimalHalfUp(unitNet, priceDecimalPlaces),
    taxAmount: roundAwayFromZero(raw.rawLineTax, taxDecimalPlaces),
  };
}

export function sumDisplayLineNetsForFooter(
  lines: ReadonlyArray<Pick<InclusiveLineDisplayAmounts, 'lineNet' | 'rawLineNet'>>,
  roundThenAggregate: boolean,
): number {
  return lines.reduce(
    (acc, line) => acc + (roundThenAggregate ? line.lineNet : line.rawLineNet),
    0,
  );
}

/** Guarantees footer net + tax equals rounded grand total in the UI. */
export function balancedFooterNetBeforeTax(
  displayGrandTotal: number,
  displayTotalTax: number,
): number {
  return displayGrandTotal - displayTotalTax;
}

export function distributeAmount(totalAmount: number, weights: number[]): number[] {
  const result = new Array(weights.length).fill(0);
  if (totalAmount <= 0 || weights.length === 0) return result;
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0) return result;
  
  let sumDistributed = 0;
  for (let i = 0; i < weights.length - 1; i++) {
    const share = totalAmount * (weights[i] / totalWeight);
    result[i] = share;
    sumDistributed += share;
  }
  result[weights.length - 1] = totalAmount - sumDistributed;
  return result;
}

export interface CoreDocumentLineCalculationContext {
  index: number;
  rawLineNet: number;
  vatPercent: number;
}

export interface CoreDocumentLineTotals {
  index: number;
  distributedHeaderDiscount: number;
  distributedHeaderCharge: number;
  rawTaxableAmount: number;
  rawLineTax: number;
  rawLineTotal: number;
}

export interface CoreDocumentTotals {
  sumBeforeHeader: number;
  headerDiscountTotal: number;
  headerChargeTotal: number;
  rawTotalNet: number;
  rawTotalVat: number;
  rawGrandTotal: number;
  lines: CoreDocumentLineTotals[];
}

export function calculateDocumentTotalsCore(
  lines: CoreDocumentLineCalculationContext[],
  headerDiscountAmount: number,
  headerDiscountIsPercent: boolean,
  headerChargeAmount: number,
  headerChargeIsPercent: boolean
): CoreDocumentTotals {
  const sumBeforeHeader = lines.reduce((sum, line) => sum + line.rawLineNet, 0);

  const headerDiscountTotal = headerDiscountIsPercent 
    ? (sumBeforeHeader * headerDiscountAmount) / 100 
    : headerDiscountAmount;
  
  const headerChargeBasis = Math.max(0, sumBeforeHeader - headerDiscountTotal);
  const headerChargeTotal = headerChargeIsPercent
    ? (headerChargeBasis * headerChargeAmount) / 100
    : headerChargeAmount;

  const weights = lines.map(l => l.rawLineNet);
  const distributedDiscounts = distributeAmount(headerDiscountTotal, weights);
  const distributedCharges = distributeAmount(headerChargeTotal, weights);

  let rawTotalNet = 0;
  let rawTotalVat = 0;
  
  const lineTotals: CoreDocumentLineTotals[] = lines.map((line, i) => {
    const rawTaxableAmount = Math.max(0, line.rawLineNet - distributedDiscounts[i] + distributedCharges[i]);
    const rawLineTax = rawTaxableAmount * (line.vatPercent / 100);
    const rawLineTotal = rawTaxableAmount + rawLineTax;
    
    rawTotalNet += rawTaxableAmount;
    rawTotalVat += rawLineTax;

    return {
      index: line.index,
      distributedHeaderDiscount: distributedDiscounts[i],
      distributedHeaderCharge: distributedCharges[i],
      rawTaxableAmount,
      rawLineTax,
      rawLineTotal
    };
  });

  return {
    sumBeforeHeader,
    headerDiscountTotal,
    headerChargeTotal,
    rawTotalNet,
    rawTotalVat,
    rawGrandTotal: rawTotalNet + rawTotalVat,
    lines: lineTotals
  };
}
